import { useState, useEffect } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import {
  Brand,
  Button,
  Container,
  Divider,
  Form,
  FormField,
  Group,
  Link,
  Loader,
  SecuredTextField,
  SectionBannerCloseButton,
  SectionBannerContent,
  SectionBannerDescription,
  SectionBannerIcon,
  SectionBannerRoot,
  SectionBannerTitle,
  Text,
  TextField,
  useMelioForm,
  useBreakpointValue,
} from '@melio/penny';
import { object, string } from 'yup';

import { login } from '../../api/auth';
import { useAnalytics, type Cta } from '../../analytics';
import { getEntryPoint, setEntryPoint, setIdentificationMethod, type IdentificationMethod } from '../../session';
import { useMfaNavigation } from '../../hooks/useMfaNavigation';
import { useManualEntrySignal } from '../../hooks/useManualEntrySignal';
import { useStoreRedirectParam, completeAuthRedirect } from '../../hooks/useRedirectParam';
import { identifyUser } from '../../monitoring';
import { removeSessionStorageItemSafely, setSessionStorageItemSafely } from '../../utils/storage';
import {
  isSsoProvider,
  navigateToGoogleSSO,
  navigateToXeroSSO,
  navigateToIntuitSSO,
  redirectSsoError,
  startSso,
  SSO_DIRECT_ENTRY_KEY,
} from '../../hooks/useSsoNavigation';
import { TermsFooter } from '../';
import { SsoButtons } from '../SsoButtons/SsoButtons';
import { DomainTransitionBanner } from '../DomainTransitionBanner/DomainTransitionBanner';
import { dismissDomainTransitionBanner } from '../DomainTransitionBanner/domainTransitionBannerCookie';
import splitscreenImage from '../../images/splitscreen.png';

interface LoginFormModel {
  email: string;
  password: string;
}

type LoginErrorType = 'invalidCredentials' | 'tooManyAttempts' | 'accountBlocked' | 'generalError';

const SSO_ERROR_MESSAGES: Record<string, string> = {
  sso_error: 'Sign-in failed. Please try again or use a different sign-in method.',
  sso_email_not_verified: "Your account email isn't verified with the provider. Please verify it and try again.",
  unsupported_sso_provider: 'This sign-in method is not supported. Please try again or use a different sign-in method.',
};

const useLoginValidationSchema = () => {
  return object().shape({
    email: string().email('Please enter a valid email address').required('Enter a valid email address.'),
    password: string().required('Enter your account password.'),
  });
};

export const LoginPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { navigateToMfaChallenge } = useMfaNavigation();
  const isMobile = useBreakpointValue({ xs: true, s: false });
  const { track } = useAnalytics();
  useStoreRedirectParam();
  const { manualEntryRef: passwordManualEntryRef, onKeyDown: handlePasswordKeyDown, onFocus: handlePasswordFocus } = useManualEntrySignal();

  const ssoProviderParam = searchParams.get('sso_provider');
  // Whether THIS page load's URL carried its own redirect param — captured
  // once at mount via a lazy initializer, since useStoreRedirectParam strips
  // `redirect` from searchParams shortly after mount. Re-deriving this from
  // the live searchParams on every render would flip it from true to false
  // once stripped, changing the effect's dependency array and firing a
  // second, conflicting startSso call. auth_redirect in sessionStorage is
  // also never cleared on error paths, so without this check a page load
  // with no OAuth context of its own could redirect errors using a
  // previous, unrelated caller's stale redirect_uri/state.
  const [hasFreshOAuthContext] = useState(() => searchParams.has('redirect'));

  useEffect(() => {
    if (!ssoProviderParam) {
      return;
    }

    if (!isSsoProvider(ssoProviderParam)) {
      // The presence of sso_provider at all (even an invalid value) makes this a
      // direct-entry attempt — but only mark it so redirectSsoError sends the
      // error to the caller's redirect_uri if this load actually has one.
      if (hasFreshOAuthContext) {
        setSessionStorageItemSafely(SSO_DIRECT_ENTRY_KEY, 'true');
      } else {
        removeSessionStorageItemSafely(SSO_DIRECT_ENTRY_KEY);
      }
      redirectSsoError('invalid_request', 'unsupported_sso_provider', '/sign-in');
      return;
    }

    void startSso(ssoProviderParam, { isDirectEntry: hasFreshOAuthContext });
  }, [ssoProviderParam, hasFreshOAuthContext]);

  const [isLoading, setIsLoading] = useState(false);
  // Any non-empty `error` value gets a banner — falling back to the generic
  // sso_error message for codes we don't have specific copy for yet, rather
  // than silently showing nothing. Upstream callers (auth-service,
  // partnerships-api) forward real provider/internal error codes here, and
  // that set can grow without a matching auth-app release.
  const [ssoErrorMessage, setSsoErrorMessage] = useState<string | null>(() => {
    const error = searchParams.get('error');
    return error ? (SSO_ERROR_MESSAGES[error] ?? SSO_ERROR_MESSAGES.sso_error) : null;
  });
  const [errorType, setErrorType] = useState<LoginErrorType | null>(null);

  useEffect(() => {
    const error = searchParams.get('error');
    if (error) {
      searchParams.delete('error');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);
  const schema = useLoginValidationSchema();
  // Set entry point on mount
  setEntryPoint('sign-in');
  document.title = 'Sign in to Melio';

  // Track page view (skipped when auto-starting SSO — the sign-in page is never shown)
  useEffect(() => {
    if (ssoProviderParam) {
      return;
    }

    track({
      eventName: 'Connection-View',
      properties: {
        PageName: 'sign-in-to-melio',
        Flow: 'sign-in',
        Intent: 'sign-in',
        EntryPoint: getEntryPoint(),
      },
    });
  }, [track, ssoProviderParam]);

  const trackClick = (cta: Cta, identificationMethod: IdentificationMethod = 'manually') => {
    track({
      eventName: 'Connection-Click',
      properties: {
        PageName: 'sign-in-to-melio',
        Flow: 'sign-in',
        Intent: 'sign-in',
        EntryPoint: getEntryPoint(),
        Cta: cta,
        IdentificationMethod: identificationMethod,
        EmailInteraction: !!formState.touchedFields.email,
        PasswordInteraction: !!formState.touchedFields.password,
        PasswordManualEntry: passwordManualEntryRef.current,
      },
    });
  };

  const trackStatus = (
    statusType: 'success' | 'failure',
    cta: Cta,
    identificationMethod: IdentificationMethod = 'manually',
    errorType?: 'login-info-incorrect'
  ) => {
    track({
      eventName: 'Connection-Status',
      properties: {
        PageName: 'sign-in-to-melio',
        Flow: 'sign-in',
        Intent: 'sign-in',
        EntryPoint: getEntryPoint(),
        Cta: cta,
        IdentificationMethod: identificationMethod,
        StatusType: statusType,
        ...(errorType && { ErrorType: errorType }),
      },
    });
  };

  const { formProps, registerField, handleSubmit, formState } = useMelioForm<LoginFormModel>({
    onSubmit: async (data) => {
      setIsLoading(true);
      setErrorType(null);
      setIdentificationMethod('manually');
      trackClick('continue', 'manually');

      const result = await login({ email: data.email, password: data.password });

      switch (result.type) {
        case 'invalidCredentials':
          trackStatus('failure', 'continue', 'manually', 'login-info-incorrect');
          setErrorType('invalidCredentials');
          setIsLoading(false);
          return;

        case 'tooManyAttempts':
          trackStatus('failure', 'continue', 'manually', 'login-info-incorrect');
          setErrorType('tooManyAttempts');
          setIsLoading(false);
          return;

        case 'accountBlocked':
          trackStatus('failure', 'continue', 'manually', 'login-info-incorrect');
          setErrorType('accountBlocked');
          setIsLoading(false);
          return;

        case 'error':
          trackStatus('failure', 'continue', 'manually', 'login-info-incorrect');
          setErrorType('generalError');
          setIsLoading(false);
          return;

        case 'mfaRequired':
          trackStatus('success', 'continue', 'manually');
          navigateToMfaChallenge({
            mfaToken: result.mfaToken,
            authenticators: result.authenticators,
            challengedAuthenticators: result.challengedAuthenticators,
          });
          setIsLoading(false);
          return;

        case 'success':
          identifyUser(result.user);
          trackStatus('success', 'continue', 'manually');
          dismissDomainTransitionBanner();
          completeAuthRedirect();
          setIsLoading(false);
          return;

        default: {
          const _exhaustiveCheck: never = result;
          return _exhaustiveCheck;
        }
      }
    },
    schema,
    defaultValues: {
      email: '',
      password: '',
    },
  });

  const handleGoogleSignIn = () => {
    setIdentificationMethod('google');
    trackClick('sign-in-with-google', 'google');
    navigateToGoogleSSO();
  };

  const handleXeroSignIn = () => {
    setIdentificationMethod('xero');
    trackClick('sign-in-with-xero', 'xero');
    navigateToXeroSSO();
  };

  const handleIntuitSignIn = () => {
    setIdentificationMethod('intuit');
    trackClick('sign-in-with-intuit', 'intuit');
    navigateToIntuitSSO();
  };

  const handleForgotPassword = (e: React.MouseEvent) => {
    e.preventDefault();
    trackClick('forgot-password', 'manually');
    navigate({ pathname: '/forgot-password', search: location.search });
  };

  const handleSignUp = (e: React.MouseEvent) => {
    if (e.ctrlKey || e.metaKey || e.shiftKey || e.button !== 0) return;
    e.preventDefault();
    trackClick('sign-up', 'manually');
    navigate({ pathname: '/create-account', search: location.search });
  };

  const onSubmit = handleSubmit();

  const handleFormKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      onSubmit();
    }
  };

  if (ssoProviderParam) {
    return (
      <Container data-testid="login-page" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}>
        <Loader />
      </Container>
    );
  }

  return (
    <Container
      data-testid="login-page"
      backgroundColor="default"
      paddingX={isMobile ? undefined : 'xl'}
      paddingY={isMobile ? undefined : 'l'}
      style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}
    >
      {/* Full-width logo header on mobile */}
      {isMobile ? (
        <Group variant="vertical" spacing="none" alignItems="stretch">
          <Container paddingX="s" paddingY="s" alignItems="center">
            <Brand type="melio" data-testid="melio-brand" />
          </Container>
          <Divider />
        </Group>
      ) : null}
      <Container
        overflow="visible"
        paddingX={isMobile ? 's' : undefined}
        paddingY={isMobile ? 's' : undefined}
        style={{
          display: 'flex',
          flex: 1,
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: isMobile ? 'flex-start' : 'center',
        }}
      >
        <Group variant="vertical" alignItems="center" width="full">
          {/* Domain transition banner - above the card */}
          <DomainTransitionBanner />

          {/* Error Banner - above the card */}
          {errorType === 'invalidCredentials' && (
            <Container width="full" maxWidth="480px" paddingBottom="xs">
              <SectionBannerRoot variant="critical" data-testid="login-error-banner">
                <SectionBannerIcon />
                <SectionBannerContent>
                  <SectionBannerTitle>We couldn&apos;t sign you in</SectionBannerTitle>
                  <SectionBannerDescription>Make sure your email and password are correct, then try again.</SectionBannerDescription>
                  <Link
                    href="#"
                    variant="standalone"
                    size="medium"
                    label="Reset password"
                    onClick={handleForgotPassword}
                    data-testid="reset-password-link"
                  />
                </SectionBannerContent>
                <SectionBannerCloseButton onClick={() => setErrorType(null)} />
              </SectionBannerRoot>
            </Container>
          )}

          {errorType === 'tooManyAttempts' && (
            <Container width="full" maxWidth="480px" paddingBottom="xs">
              <SectionBannerRoot variant="critical" data-testid="login-error-banner">
                <SectionBannerIcon />
                <SectionBannerContent>
                  <SectionBannerTitle>Your account is temporarily locked</SectionBannerTitle>
                  <SectionBannerDescription>There were too many failed attempts to log in. Try again in 24 hours.</SectionBannerDescription>
                </SectionBannerContent>
                <SectionBannerCloseButton onClick={() => setErrorType(null)} />
              </SectionBannerRoot>
            </Container>
          )}

          {errorType === 'accountBlocked' && (
            <Container width="full" maxWidth="480px" paddingBottom="xs">
              <SectionBannerRoot variant="critical" data-testid="login-error-banner">
                <SectionBannerIcon />
                <SectionBannerContent>
                  <SectionBannerTitle>Your account is temporarily locked</SectionBannerTitle>
                  <SectionBannerDescription>Please try again later or contact support.</SectionBannerDescription>
                  <Link
                    href="mailto:support@melio.com"
                    variant="standalone"
                    size="medium"
                    label="Contact Support"
                    data-testid="contact-support-link"
                  />
                </SectionBannerContent>
                <SectionBannerCloseButton onClick={() => setErrorType(null)} />
              </SectionBannerRoot>
            </Container>
          )}

          {errorType === 'generalError' && (
            <Container width="full" maxWidth="480px" paddingBottom="xs">
              <SectionBannerRoot variant="critical" data-testid="login-error-banner">
                <SectionBannerIcon />
                <SectionBannerContent>
                  <SectionBannerDescription>An unexpected error occurred. Please try again.</SectionBannerDescription>
                </SectionBannerContent>
                <SectionBannerCloseButton onClick={() => setErrorType(null)} />
              </SectionBannerRoot>
            </Container>
          )}

          {ssoErrorMessage && (
            <Container width="full" maxWidth="480px" paddingBottom="xs">
              <SectionBannerRoot variant="critical" data-testid="login-error-banner">
                <SectionBannerIcon />
                <SectionBannerContent>
                  <SectionBannerDescription>{ssoErrorMessage}</SectionBannerDescription>
                </SectionBannerContent>
                <SectionBannerCloseButton onClick={() => setSsoErrorMessage(null)} />
              </SectionBannerRoot>
            </Container>
          )}

          {/* Main content - centered card with border (no border on mobile) */}
          <Container
            overflow="visible"
            width="full"
            maxWidth="480px"
            paddingX={isMobile ? undefined : 'xl'}
            paddingTop={isMobile ? undefined : 'l'}
            paddingBottom="l"
            {...(!isMobile && { border: 'regular' })}
          >
            <Group variant="vertical" spacing={isMobile ? 'm' : 'l'} alignItems="stretch">
              {/* Melio Logo - left aligned (on mobile it renders as a full-width header above) */}
              {isMobile ? null : (
                <Container alignItems="center" style={{ display: 'inline-block', transform: 'scale(1.5)', transformOrigin: 'left top' }}>
                  <Brand type="melio" data-testid="melio-brand" />
                </Container>
              )}

              {/* Header */}
              <Group variant="vertical" spacing={isMobile ? 'xxs' : 'xs'}>
                <Text textStyle={isMobile ? 'heading2Semi' : 'heading1Semi'} color="semantic.text.primary" data-testid="login-page-title">
                  Sign in
                </Text>
              </Group>

              {/* Email/Password Form */}
              <Container paddingTop={isMobile ? undefined : 's'} overflow="visible" width="full">
                <Form {...formProps} rowGap="s" onKeyDown={handleFormKeyDown} columns={1} autoComplete="on" data-testid="login-form">
                  <FormField
                    {...registerField('email')}
                    labelProps={{ label: 'Email address' }}
                    isRequired
                    size={isMobile ? 'small' : 'large'}
                    render={(fieldProps) => (
                      <TextField {...fieldProps} type="email" placeholder="" autoComplete="username" data-testid="email-input" />
                    )}
                  />

                  <Group variant="vertical" spacing="s">
                    <FormField
                      {...registerField('password')}
                      labelProps={{ label: 'Password' }}
                      isRequired
                      size={isMobile ? 'small' : 'large'}
                      render={(fieldProps) => (
                        <SecuredTextField
                          {...fieldProps}
                          type="password"
                          placeholder=""
                          autoComplete="current-password"
                          data-testid="password-input"
                          data-private
                          onKeyDown={handlePasswordKeyDown}
                          onFocus={handlePasswordFocus}
                        />
                      )}
                    />
                    <Link
                      href="#"
                      variant="standalone"
                      size="medium"
                      color="secondary"
                      label="Forgot password?"
                      onClick={handleForgotPassword}
                      data-testid="forgot-password-link"
                    />
                  </Group>

                  <FormField
                    render={() => (
                      <Container paddingTop="xs">
                        <Button
                          label="Sign in"
                          variant="primary"
                          size={isMobile ? 'medium' : 'large'}
                          isFullWidth
                          isLoading={isLoading}
                          onClick={() => onSubmit()}
                          data-testid="continue-button"
                        />
                      </Container>
                    )}
                  />
                </Form>

                {/* Sign Up Link - positioned at the bottom of the form */}
                <Container textAlign="center" width="full" paddingTop="s">
                  <Group variant="horizontal" spacing="xs" alignItems="center" justifyContent="center">
                    <Text textStyle="body2" color="semantic.text.primary">
                      New to Melio?
                    </Text>
                    <Link
                      href={`/create-account${location.search}`}
                      variant="standalone"
                      size="large"
                      color="secondary"
                      label="Create account"
                      onClick={handleSignUp}
                      data-testid="sign-up-link"
                    />
                  </Group>
                </Container>

                <Container paddingY="m">
                  <Divider label="or" />
                </Container>
                <SsoButtons
                  variant="sign-in"
                  onGoogleClick={handleGoogleSignIn}
                  onXeroClick={handleXeroSignIn}
                  onIntuitClick={handleIntuitSignIn}
                />
              </Container>
            </Group>
          </Container>
        </Group>
      </Container>

      {/* Terms Footer - at the bottom */}
      <Container
        textAlign="center"
        width="full"
        paddingTop="l"
        paddingX={isMobile ? 's' : undefined}
        paddingBottom={isMobile ? 's' : undefined}
      >
        <TermsFooter />
      </Container>

      {/* Hidden preload of split screen image for create-account page */}
      <div style={{ display: 'none' }} aria-hidden="true">
        <img src={splitscreenImage} alt="" />
      </div>
    </Container>
  );
};
